import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import LoadError from '../components/LoadError';
import { OrderTimeline } from '../components/OrderTimeline';
import { DetailCard, DetailRow, fmtDateTime } from '../components/DetailParts';
import { useToast } from '../components/Toast';
import {
  ADMIN_SETTABLE_ORDER_STATUSES,
  OrderStatus,
  ServiceOrder,
  extractApiError,
  formatAmount,
  getOrder,
  statusBadgeClass,
  statusLabel,
  updateOrderStatus,
} from '../api/orders';

/**
 * A legal service order (Legal Services Request page → View). Shows only what a legal
 * booking stores — the maintenance-request fields (urgency, preferred date,
 * time slot, description, photos, quotes) live on the Service Order view.
 */
const ServiceOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [order, setOrder] = useState<ServiceOrder | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const loaded = await getOrder(Number(id));
      // Notifications can't tell legal orders from maintenance; send those to their own page.
      if (loaded.kind === 'maintenance') {
        navigate(`/admin/service-requests/${loaded.id}`, { replace: true });
        return;
      }
      setOrder(loaded);
    } catch (err) {
      setError(extractApiError(err, 'Failed to load request'));
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (next: OrderStatus) => {
    if (!order || next === order.status) return;
    setSaving(true);
    try {
      const updated = await updateOrderStatus(order.id, next);
      setOrder((o) => (o ? { ...o, ...updated } : o));
      toast(`${order.orderRef || 'Request'} → ${statusLabel(next)}`, 'success');
    } catch (err) {
      toast(extractApiError(err, 'Failed to update status'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const back = (
    <button
      className="flex items-center gap-2 text-sm font-semibold text-g600 hover:text-ink transition-colors"
      onClick={() => navigate('/admin/orders')}
    >
      <Icon name="arrow-left" size={18} /> Back to legal services requests
    </button>
  );

  if (loading) {
    return (
      <div className="py-20 text-center text-g500">
        <Icon name="dollar-sign" size={28} color="var(--g400)" /> Loading request...
      </div>
    );
  }

  if (error || !order) {
    return (
      <LoadError
        noun="request"
        error={error}
        onRetry={load}
        backLabel="Back to legal services requests"
        onBack={() => navigate('/admin/orders')}
      />
    );
  }

  const timeline = Array.isArray(order.timeline) ? order.timeline : [];
  const serviceName = order.service?.name || order.serviceName;

  return (
    <div className="animate-fadeIn">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        {back}
        <div className="flex items-center gap-2.5">
          <span className="text-[13px] text-g500">Update status</span>
          <select
            value={order.status}
            disabled={saving}
            onChange={(e) => changeStatus(e.target.value as OrderStatus)}
            className="form-input"
            style={{ width: 170, padding: '7px 10px', fontWeight: 600, cursor: saving ? 'wait' : 'pointer' }}
          >
            {/* Keep the current status listed even when admin can't set it. */}
            {!ADMIN_SETTABLE_ORDER_STATUSES.includes(order.status) && (
              <option value={order.status} disabled>
                {statusLabel(order.status)}
              </option>
            )}
            {ADMIN_SETTABLE_ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Heading */}
      <div className="card p-6 mb-5 flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[12px] text-g500 font-semibold uppercase tracking-wider">Legal services request</div>
          <div className="text-2xl font-extrabold text-ink">{order.orderRef || `#${order.id}`}</div>
          <div className="text-[13px] text-g500 mt-0.5">Placed {fmtDateTime(order.createdAt)}</div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-[12px] text-g500">Amount</div>
            <div className="text-2xl font-extrabold text-brand">{formatAmount(order.amount)}</div>
          </div>
          <span className={statusBadgeClass(order.status)}>{statusLabel(order.status)}</span>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2 items-start">
        <div className="flex flex-col gap-5">
          <DetailCard icon="scale" title="Service">
            <DetailRow label="Legal service">
              {order.serviceId ? (
                <button
                  type="button"
                  className="text-brand hover:underline font-semibold text-right"
                  onClick={() => navigate(`/admin/legal/${order.serviceId}`)}
                >
                  {serviceName || '—'}
                </button>
              ) : (
                serviceName || '—'
              )}
            </DetailRow>
            <DetailRow label="Tier">{order.tier || '—'}</DetailRow>
          </DetailCard>

          <DetailCard icon="user" title="Customer">
            <DetailRow label="Name">
              {order.customer?.name ? (
                <button
                  type="button"
                  className="text-brand hover:underline font-semibold text-right"
                  onClick={() => navigate(`/admin/users/${order.userId}`)}
                >
                  {order.customer.name}
                </button>
              ) : (
                '—'
              )}
            </DetailRow>
            <DetailRow label="Email">
              {order.customer?.email ? (
                <a href={`mailto:${order.customer.email}`} className="hover:text-brand">
                  {order.customer.email}
                </a>
              ) : (
                '—'
              )}
            </DetailRow>
          </DetailCard>

          <DetailCard icon="credit-card" title="Payment">
            <DetailRow label="Amount">{formatAmount(order.amount)}</DetailRow>
            <DetailRow label="Payment status">
              {order.paid ? (
                <span className="inline-flex items-center gap-1" style={{ color: 'var(--green)' }}>
                  <Icon name="check-circle" size={14} color="var(--green)" /> Paid
                </span>
              ) : (
                <span className="text-g500">Unpaid</span>
              )}
            </DetailRow>
            <DetailRow label="Paid on">{fmtDateTime(order.paidAt)}</DetailRow>
          </DetailCard>
        </div>

        <DetailCard icon="calendar" title="Progress">
          <OrderTimeline timeline={timeline} />
          <div className="text-[12px] text-g400 mt-3 pt-3 border-t border-g100">
            Last updated {fmtDateTime(order.updatedAt)}
          </div>
        </DetailCard>
      </div>
    </div>
  );
};

export default ServiceOrderDetail;
